import { useState } from 'react'
import { Handshake, Pencil, Plus, Trash2 } from 'lucide-react'

import type { Referral, ReferralStatus } from '@/types/marketing'
import { REFERRAL_STATUS_LABELS, REFERRAL_STATUS_VARIANT } from '@/types/marketing'
import {
  useCreateReferral,
  useDeleteReferral,
  useReferrals,
  useUpdateReferral,
} from '@/features/marketing/hooks'
import { useCustomers } from '@/features/customers/hooks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'

const STATUS_OPTIONS = Object.entries(REFERRAL_STATUS_LABELS) as [ReferralStatus, string][]

export function ReferralsTab() {
  const { data: referrals = [], isLoading } = useReferrals()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Referral | null>(null)

  const converted = referrals.filter((r) => r.status === 'converted').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {referrals.length} referrals tracked · {converted} became customers. Referrals are also
          recorded automatically when a lead or customer carries a referrer.
        </p>
        <Button
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> New Referral
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : referrals.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No referrals yet — record one when a customer sends a prospect your way.
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referred</TableHead>
                <TableHead>Referrer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referrals.map((referral) => (
                <TableRow key={referral.id}>
                  <TableCell>
                    <div className="flex items-center gap-2 font-medium">
                      {referral.referred_name}
                      {referral.referred_customer_id && (
                        <Badge variant="default">customer</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {referral.referred_customer_id
                        ? 'linked to customer'
                        : referral.referred_lead_id
                          ? 'linked to lead'
                          : 'not yet captured'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ReferrerName referrerId={referral.referrer_id} />
                  </TableCell>
                  <TableCell>
                    <Badge variant={REFERRAL_STATUS_VARIANT[referral.status]}>
                      {REFERRAL_STATUS_LABELS[referral.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(referral.referral_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <span className="text-xs text-muted-foreground">
                      {referral.notes || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(referral)
                          setDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <ReferralDeleteButton referral={referral} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ReferralDialog open={dialogOpen} onOpenChange={setDialogOpen} referral={editing} />
    </div>
  )
}

// Referrer names resolve against the customer list.
function ReferrerName({ referrerId }: { referrerId: string }) {
  const { data: customers = [] } = useCustomers()
  const referrer = customers.find((c) => c.id === referrerId)
  if (!referrer) return <span className="text-muted-foreground">—</span>
  return <span>{`${referrer.first_name} ${referrer.last_name}`.trim() || referrer.company_name}</span>
}

function ReferralDeleteButton({ referral }: { referral: Referral }) {
  const deleteMutation = useDeleteReferral()
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setConfirmOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete referral?</DialogTitle>
            <DialogDescription>
              The record of “{referral.referred_name}” being referred is removed. Linked leads and
              customers are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await deleteMutation.mutateAsync(referral.id)
                setConfirmOpen(false)
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface ReferralFormState {
  referrer_id: string
  referred_name: string
  referred_customer_id: string
  status: string
  notes: string
}

const EMPTY_FORM: ReferralFormState = {
  referrer_id: '',
  referred_name: '',
  referred_customer_id: 'none',
  status: 'pending',
  notes: '',
}

function ReferralDialog({
  open,
  onOpenChange,
  referral,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  referral: Referral | null
}) {
  const createMutation = useCreateReferral()
  const updateMutation = useUpdateReferral()
  const pending = createMutation.isPending || updateMutation.isPending
  const { data: customers = [] } = useCustomers()

  const [form, setForm] = useState<ReferralFormState>(EMPTY_FORM)
  const set = <K extends keyof ReferralFormState>(key: K, value: ReferralFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  // Reset the form whenever the dialog opens or the target referral changes
  // ("adjust state during render" idiom, same as the segment dialog).
  const formKey = `${open}:${referral?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      setForm(
        referral
          ? {
              referrer_id: referral.referrer_id,
              referred_name: referral.referred_name,
              referred_customer_id: referral.referred_customer_id || 'none',
              status: referral.status,
              notes: referral.notes,
            }
          : EMPTY_FORM,
      )
    }
  }

  const submit = async () => {
    const body = {
      referrer_id: form.referrer_id,
      referred_name: form.referred_name,
      referred_customer_id: form.referred_customer_id === 'none' ? '' : form.referred_customer_id,
      status: form.status,
      notes: form.notes,
    }
    try {
      if (referral) {
        await updateMutation.mutateAsync({ id: referral.id, body })
      } else {
        await createMutation.mutateAsync(body)
      }
      onOpenChange(false)
    } catch {
      // keep the dialog open; the global error toast shows the message
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="h-4 w-4" /> {referral ? 'Edit referral' : 'New referral'}
          </DialogTitle>
          <DialogDescription>
            One of your customers sent a prospect your way. Track the relationship to its outcome.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Select value={form.referrer_id || 'none'} onValueChange={(v) => set('referrer_id', v)}>
            <SelectTrigger className="col-span-2">
              <SelectValue placeholder="Referrer (existing customer)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>
                Choose a customer…
              </SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {`${c.first_name} ${c.last_name}`.trim() || c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Referred person's name"
            className="col-span-2"
            value={form.referred_name}
            onChange={(e) => set('referred_name', e.target.value)}
          />
          <Select
            value={form.referred_customer_id}
            onValueChange={(v) => set('referred_customer_id', v)}
          >
            <SelectTrigger className="col-span-2">
              <SelectValue placeholder="Linked customer (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not a customer yet</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {`${c.first_name} ${c.last_name}`.trim() || c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form.status} onValueChange={(v) => set('status', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Notes"
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!form.referrer_id || !form.referred_name.trim() || pending}
            onClick={submit}
          >
            {pending ? 'Saving…' : referral ? 'Save' : 'Record referral'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
